import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/utils/toast";
import { getApiUrl } from "@/utils/api";
import { useTranslation } from "react-i18next";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Category } from "@/types";

interface FacilitiesCategoriesFormProps {
  facilityId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface FacilityCategory {
  id: number;
  facility: number;
  category: number;
  category_details: {
    id: number;
    name: string;
  };
  is_active: boolean;
}

const categorySchema = z.object({
  categories: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      is_active: z.boolean().default(false),
    }),
  ),
});

type CategoryFormValues = z.infer<typeof categorySchema>;

export default function FacilitiesCategoriesForm({
  facilityId,
  onSuccess,
  onCancel,
}: FacilitiesCategoriesFormProps): ReactElement {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Array<Category>>([]);
  const [userCompanyId, setUserCompanyId] = useState<number | null>(null);

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      categories: [],
    },
  });

  // Fetch all categories
  const { data: allCategories } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("categories/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();

      // Ensure we return an array
      if (data && data.results && Array.isArray(data.results)) {
        return data.results;
      } else if (Array.isArray(data)) {
        return data;
      } else {
        console.error("Unexpected categories data format:", data);
        return [];
      }
    },
  });

  // Fetch facility categories
  const {
    data: facilityCategories,
    isLoading: isLoadingCompanyCategories,
    isError,
    error,
  } = useQuery({
    queryKey: ["facility-categories", facilityId],
    queryFn: async () => {
      if (!facilityId) {
        return [];
      }
      try {
        const url = getApiUrl(
          `facility-categories/by_facility/?facility_id=${facilityId}`,
        );
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to fetch company categories: ${response.status} ${errorText}`,
          );
        }

        const data = await response.json();

        // Ensure we return an array
        if (data && data.results && Array.isArray(data.results)) {
          return data.results;
        } else if (Array.isArray(data)) {
          return data;
        } else {
          console.error("Unexpected company categories data format:", data);
          return [];
        }
      } catch (err) {
        throw err;
      }
    },
    enabled: !!facilityId,
  });

  // Handle error from company categories query
  useEffect(() => {
    if (isError && error) {
      console.error("Query error:", error);
      toast.error(`Failed to fetch company categories: ${error.message}`);
    }
  }, [isError, error]);

  // Process categories when both queries are complete
  useEffect(() => {
    if (allCategories && facilityCategories) {
      // Ensure allCategories is an array before mapping
      if (!Array.isArray(allCategories)) {
        console.error("allCategories is not an array:", allCategories);
        return;
      }

      const categoriesWithStatus = allCategories.map((category: Category) => {
        // Make sure facilityCategories is an array before using find
        const facilityCategory = Array.isArray(facilityCategories)
          ? facilityCategories.find(
              (cc: FacilityCategory) => cc.category === category.id,
            )
          : undefined;

        return {
          id: category.id,
          name: category.name,
          is_active: facilityCategory ? facilityCategory.is_active : false,
        };
      });

      setCategories(categoriesWithStatus);
      form.reset({
        categories: categoriesWithStatus,
      });
    }
  }, [allCategories, facilityCategories, form]);

  // Mutation to update company categories
  const mutation = useMutation({
    mutationFn: async (values: CategoryFormValues) => {
      if (!facilityId) throw new Error("Facility ID is required");

      // Create or update company categories
      const updatePromises = values.categories.map(async (category) => {
        // Check if the company-category relation already exists
        const existingFacilityCategory = Array.isArray(facilityCategories)
          ? facilityCategories.find(
              (cc: FacilityCategory) => cc.category === category.id,
            )
          : undefined;

        const url = existingFacilityCategory
          ? getApiUrl(`facility-categories/${existingFacilityCategory.id}/`)
          : getApiUrl("facility-categories/");

        const method = existingFacilityCategory ? "PATCH" : "POST";

        const body = existingFacilityCategory
          ? { is_active: category.is_active }
          : {
              facility: facilityId,
              category: category.id,
              is_active: category.is_active,
            };

        const response = await fetch(url, {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorData = await response.text();
          console.error(`Error response from ${url}:`, errorData);
          throw new Error(`Failed to update category: ${errorData}`);
        }

        const responseData = await response.json();
        return responseData;
      });

      return Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["facility-categories", facilityId],
      });

      // Show success toast
      toast.success("Categories updated successfully");
      onSuccess();
    },
    onError: (error: Error) => {
      console.error("Error updating categories:", error);

      // Show error toast
      toast.error(error.message);
    },
  });

  const onSubmit = (values: CategoryFormValues) => {
    mutation.mutate(values);
  };

  if (isLoadingCompanyCategories || !facilityId) {
    return (
      <div className="flex justify-center items-center h-64">Loading...</div>
    );
  }

  const handleToggleCategory = (index: number, checked: boolean) => {
    const updatedCategories = [...categories];
    updatedCategories[index].is_active = checked;
    setCategories(updatedCategories);
    form.setValue("categories", updatedCategories);
  };

  // Render the component
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full pb-24 overflow-y-auto"
      >
        {categories.length === 0 ? (
          <div className="flex justify-center items-center h-64 text-gray-500">
            No categories available
          </div>
        ) : (
          <div className="space-y-6 overflow-y-auto flex-1 px-4">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className="flex items-center justify-between py-4 border-b border-gray-200"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                      {category.name.charAt(0)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {category.name}
                    </h3>
                  </div>
                </div>
                <div>
                  <Switch
                    checked={category.is_active}
                    onCheckedChange={(checked) =>
                      handleToggleCategory(index, checked)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Fixed Save button at the bottom */}
        <div className="fixed bottom-0 left-0 right-0 py-4 px-4 bg-white border-t border-gray-200 shadow-md">
          <div className="flex justify-end space-x-4">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              type="submit"
              className="max-w-md bg-primary hover:bg-accent text-white font-semibold py-3 rounded-lg shadow-md"
              disabled={mutation.isPending || categories.length === 0}
            >
              {mutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
